import { useState, useMemo } from 'react';
import { View, StyleSheet, Pressable, ScrollView } from 'react-native';
import { Navigate, useNavigate, useParams } from 'react-router';
import { Text, Icon, theme, styles as baseStyles, SpeechBubble, Button } from '@socrait/react';

import { Period, Student } from '~/services/types';
import { MainLayout } from '~/layouts/MainLayout';
import { preferenceStore } from '~/store/preferenceStore';
import { usePeriodListAllQuery } from '~/services/period';
import { Loading } from '~/components/Loading';

import { TabBar, type TabConfig } from '~/components/TabBar';

import { OverviewTab } from './OverviewTab';
import { CustomMessagesTab } from './CustomMessagesTab';
import { DataLogTab } from './DataLogTab';
import { findStudentBySlug, getRosterPath, getStudentDataPath, SLUG_TO_TAB, type Tab } from './routing';

const TABS: readonly TabConfig<Tab>[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'customMessages', label: 'Custom Messages' },
  { key: 'dataLog', label: 'Data Log' },
];

export function StudentDataScreen() {
  const { periodShortName, studentSlug, tab: tabSlug } = useParams();
  const navigate = useNavigate();
  const { data, status } = usePeriodListAllQuery();
  const { periods } = data || {};

  const activePeriod = periods?.find((p: Period) => p.shortName === periodShortName) || periods?.[0];
  const students = useMemo(() => activePeriod?.students ?? [], [activePeriod]);
  const student = useMemo(
    () => (studentSlug ? findStudentBySlug(students, studentSlug) : undefined),
    [students, studentSlug],
  );

  const activeTab: Tab = tabSlug ? (SLUG_TO_TAB[tabSlug] ?? 'overview') : 'overview';

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const sortedStudents = useMemo(
    () => [...students].sort((a: Student, b: Student) => a.voiceName.localeCompare(b.voiceName)),
    [students],
  );

  const hasSeenStudentSwitcherWhatsNew = preferenceStore((s) => s.hasSeenStudentSwitcherWhatsNew);
  const setHasSeenStudentSwitcherWhatsNew = preferenceStore((s) => s.setHasSeenStudentSwitcherWhatsNew);

  if (status === 'pending') {
    return (
      <MainLayout>
        <Loading />
      </MainLayout>
    );
  }

  if (!activePeriod) {
    return <Navigate to="/review" replace />;
  }

  // Unknown student → bounce back to the roster for this period. Done before the tab-slug check
  // so we don't construct a redirect URL with an unresolved slug.
  if (!student) {
    return <Navigate to={getRosterPath(activePeriod.shortName)} replace />;
  }

  // Unknown tab slug → drop the slug and render overview.
  if (tabSlug && !SLUG_TO_TAB[tabSlug]) {
    return <Navigate to={getStudentDataPath(activePeriod.shortName, student.voiceName)} replace />;
  }

  const onClose = () => navigate(getRosterPath(activePeriod.shortName));

  const handleTriggerPress = () => {
    if (!isDropdownOpen && !hasSeenStudentSwitcherWhatsNew) {
      setHasSeenStudentSwitcherWhatsNew(true);
    }
    setIsDropdownOpen((prev) => !prev);
  };

  const handleStudentSelect = (s: Student) => {
    setIsDropdownOpen(false);
    navigate(getStudentDataPath(activePeriod.shortName, s.voiceName, activeTab));
  };

  const handleTabChange = (next: Tab) => {
    navigate(getStudentDataPath(activePeriod.shortName, student.voiceName, next));
  };

  return (
    <MainLayout>
      <View style={styles.container}>
        {isDropdownOpen && <Pressable style={styles.dropdownOverlay} onPress={() => setIsDropdownOpen(false)} />}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.headerText}>{activePeriod.name} - </Text>
            <View style={styles.switcherWrapper}>
              <Pressable
                style={styles.switcherTrigger}
                onPress={handleTriggerPress}
                accessibilityLabel="Switch student">
                <Text style={[baseStyles.boldText, styles.headerText]}>{student.voiceName}</Text>
                <Icon type="downChevron" width={14} height={9} />
              </Pressable>
              {isDropdownOpen && (
                <View style={styles.dropdown}>
                  <ScrollView style={styles.dropdownScroll} showsVerticalScrollIndicator={true}>
                    {sortedStudents.map((s: Student) => (
                      <Pressable
                        key={s.id}
                        style={[styles.dropdownItem, s.id === student.id && styles.dropdownItemActive]}
                        onPress={() => handleStudentSelect(s)}>
                        <Text style={[styles.dropdownItemText, s.id === student.id && styles.dropdownItemTextActive]}>
                          {s.voiceName}
                        </Text>
                      </Pressable>
                    ))}
                  </ScrollView>
                </View>
              )}
              {!hasSeenStudentSwitcherWhatsNew && !isDropdownOpen && (
                <View style={styles.switcherBubble}>
                  <SpeechBubble
                    arrowPlacement="top-left"
                    arrowOffset={20}
                    onDismiss={() => setHasSeenStudentSwitcherWhatsNew(true)}
                    bubbleStyle={styles.bubble}>
                    <View>
                      <Text style={styles.bubbleHeading}>What's New</Text>
                      <Text style={styles.bubbleBody}>Quickly switch between students without leaving this screen</Text>
                      <Button
                        name="Got it"
                        onPress={() => setHasSeenStudentSwitcherWhatsNew(true)}
                        containerStyle={styles.gotItButton}
                        fontStyle={styles.gotItButtonText}
                      />
                    </View>
                  </SpeechBubble>
                </View>
              )}
            </View>
          </View>
          <View style={styles.headerRight}>
            <TabBar activeTab={activeTab} tabs={TABS} onTabChange={handleTabChange} />
            <Pressable onPress={onClose} style={styles.closeButton} accessibilityLabel="Close">
              <Icon type="close" />
            </Pressable>
          </View>
        </View>
        <View style={styles.content}>
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'customMessages' && <CustomMessagesTab />}
          {activeTab === 'dataLog' && <DataLogTab />}
        </View>
      </View>
    </MainLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  dropdownOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 5,
  },
  header: {
    alignItems: 'center',
    borderBottomColor: theme.colors.gray,
    borderBottomWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.xlarge * 2,
    paddingTop: theme.spacing.xxlarge,
    paddingBottom: theme.spacing.large,
    zIndex: 10,
  },
  headerLeft: {
    alignItems: 'center',
    flexDirection: 'row',
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  headerText: {
    fontSize: theme.fontSizes.large,
  },
  switcherWrapper: {
    position: 'relative',
    flexShrink: 1,
    minWidth: 0,
  },
  switcherTrigger: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.small,
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: theme.spacing.small,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.medium,
    borderWidth: 1,
    borderColor: theme.colors.gray,
    minWidth: 200,
    maxHeight: 280,
    zIndex: 10,
    shadowColor: theme.colors.navy,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
  },
  dropdownScroll: {
    maxHeight: 280,
  },
  dropdownItem: {
    paddingHorizontal: theme.spacing.large,
    paddingVertical: theme.spacing.medium,
  },
  dropdownItemActive: {
    backgroundColor: theme.colors.lightGray,
  },
  dropdownItemText: {
    fontSize: theme.fontSizes.small,
    color: theme.colors.navy,
  },
  dropdownItemTextActive: {
    fontWeight: theme.fontWeights.bold,
  },
  switcherBubble: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: theme.spacing.small,
    width: 275,
    zIndex: 10,
  },
  bubble: {
    paddingVertical: theme.spacing.large,
  },
  bubbleHeading: {
    color: theme.colors.white,
    fontWeight: theme.fontWeights.bold,
    marginBottom: theme.spacing.small,
  },
  bubbleBody: {
    color: theme.colors.white,
    fontSize: theme.fontSizes.small,
    marginBottom: theme.spacing.medium,
  },
  gotItButton: {
    alignSelf: 'flex-start',
    height: 30,
    paddingHorizontal: theme.spacing.medium,
    marginTop: theme.spacing.medium,
  },
  gotItButtonText: {
    fontSize: theme.fontSizes.xsmall,
  },
  headerRight: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.large,
    flexShrink: 0,
  },
  closeButton: {
    padding: theme.spacing.medium,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.spacing.xxlarge,
    padding: theme.spacing.xxlarge,
    overflow: 'hidden',
  },
});
